import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { AUTH_ROUTES, PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/luggage-shipping.jpg'),
    alt: '/luggage shipping page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: '/luggage shipping page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Luggage <span>Shipping Made</span> <span>Easy with ShipAndStorage</span>',
  description:
    'Travel smarter with ShipAndStorage. Whether it’s luggage, golf clubs, or skis, we make shipping easy, affordable, and reliable.',
  image: {
    url: '/images/v2/pages/main-pages/luggage-shipping/hero-image.jpg',
    alt: 'luggage shipping page'
  },
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Ship Your Luggage' }
}

export const whyWeSectionContent = {
  title: 'How Does Our Luggage Shipping Work?',

  cards: [
    {
      id: 1,
      title: 'Book Your Shipment',
      description: 'Select your shipping preferences using our intuitive online booking platform.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/book-your-shipment.svg',
        alt: 'Book Your Shipment service'
      }
    },
    {
      id: 2,
      title: 'Pack and Prepare',
      description: 'Use sturdy packaging, attach your label, and secure items for transit.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/pack-and-prepare.svg',
        alt: 'Pack and Prepare service'
      }
    },
    {
      id: 3,
      title: 'Pickup or Drop-Off ',
      description: 'Choose between convenient home pickup or local drop-off locations nearby.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/pickup-or-drop-off.svg',
        alt: 'Pickup or Drop-Off  service'
      }
    },
    {
      id: 4,
      title: 'Receive Items',
      description: 'Receive your items promptly at your destination or a nearby pickup location.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/receive-items.svg',
        alt: 'Receive Items service'
      }
    }
  ]
}

export const HelperSectionContent = {
  title: 'How to Pack Your Luggage for Shipping',
  description: 'Proper packing ensures the safety of your belongings during transit.',

  articles: [
    {
      content: [
        {
          title: 'Choose the Right Luggage',
          description: 'Use a sturdy suitcase or box that can handle the journey.'
        },
        {
          title: 'Pack Strategically',
          points: [
            'Secure fragile items with bubble wrap or padding.',
            'Avoid overloading or exceeding weight limits.',
            'Close or zip all pockets securely.'
          ]
        },
        {
          title: 'Secure the Handle',
          description: 'Tape down the pull handle to avoid damage.'
        },
        {
          title: 'Prohibited Items',
          description: 'Do not pack aerosols, alcohol, batteries, or other restricted items.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/how-to-pack-your-luggage-for-shipping.jpg',
        alt: 'Prepare Your Items feature'
      }
    }
  ]
}

export const TwoCardsSectionContent = {
  title: 'How to Lock Your Luggage',

  cards: [
    {
      id: 1,
      title: 'Domestic Shipments',
      description:
        'To improve the security of your luggage when traveling within the country, use TSA-approved locks or strong cable ties. TSA-approved locks can be opened by security without breaking them, making it easy for them to check your bags. Cable ties also work well as a temporary option, helping to keep your luggage closed and safe from unwanted access after inspections.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/domestic-shipments.png',
        alt: 'Domestic Shipments'
      }
    },
    {
      id: 2,
      title: 'International Shipments',
      description:
        'For travel outside the country, you should only use cable ties to lock your luggage because customs officials may need to check it. Cable ties are light, flexible, and easy to use, helping to keep your bag closed while allowing customs to open it without problems or damaging your belongings. This way, you can avoid delays during your journey.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/international-shipments.png',
        alt: 'International Shipments'
      }
    }
  ]
}

export const howToLabelYourLuggageSectionContent = {
  title: 'How to Label Your Luggage',
  description: 'Proper labeling ensures your luggage reaches its destination without delays.',

  articles: [
    {
      content: [
        'Place the shipping label and necessary documents inside a label pouch with the tracking barcode facing out.',
        'Attach the pouch securely to a luggage tag and fasten it to the top handle.',
        'Add an additional copy of the label and documents inside your luggage for backup.',
        'Avoid placing your luggage inside a box, as this may incur extra shipping fees.',
        'Please do not affix labels directly onto the surface of your luggage.'
      ],
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/how-to-label-your-luggage.png',
        alt: 'Prepare Your Items feature'
      }
    }
  ]
}

export const howToMeasureWeightAndDimensionsSectionContent = {
  title: 'How to Measure Weight and Dimensions',
  description: "To ensure accurate shipping costs, it's important to measure your luggage correctly.",

  articles: [
    {
      content: [
        {
          title: 'Check Size and Weight Limits',
          points: [
            '<strong>Use a Digital Scale:</strong> Weigh your luggage or box for an accurate measurement.',
            '<strong>Report Actual Weight:</strong> Ensure you report the exact weight to avoid any additional charges.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/how-to-measure-weight-and-dimensions.png',
        alt: 'Prepare Your Items feature'
      }
    },
    {
      content: [
        {
          title: 'Measuring Dimensions',
          description: 'Carriers measure luggage or boxes using length and girth',
          points: [
            '<strong>Length:</strong> Measure the longest side of your luggage or box.',
            '<strong>Girth:</strong> Measure the height and width of your luggage or box, then add them together.'
          ]
        },
        {
          title: 'Important Tips',
          points: [
            '<strong>Measure at the Widest Point:</strong> Always measure the widest part of your luggage or box.',
            '<strong>Check Carrier Limits:</strong> Make sure your luggage falls within the weight and size limits to avoid extra fees.'
          ]
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/how-to-measure-weight-and-dimensions2.png',
        alt: 'Prepare Your Items feature'
      }
    }
  ]
}

export const whyChooseUsSectionContent = {
  title: 'Why Choose ShipAndStorage for Luggage Shipping?',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Start Luggage Shipping' },

  cards: [
    {
      id: 1,
      title: 'Affordable Shipping Rates',
      description: 'Enjoy cost-effective pricing without sacrificing quality or reliability.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/affordable-shipping-rates.svg',
        alt: 'Affordable Shipping Rates service'
      }
    },
    {
      id: 2,
      title: 'Flexible Scheduling Options',
      description: 'Choose pickup and delivery times that perfectly suit your plans.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/flexible-scheduling-options.svg',
        alt: 'Flexible Scheduling Options service'
      }
    },
    {
      id: 3,
      title: 'Global Shipping Network',
      description: 'Ship your luggage to over 220 international destinations effortlessly.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/global-shipping-network.svg',
        alt: 'Global Shipping Network service'
      }
    },
    {
      id: 4,
      title: 'Dedicated Customer Support',
      description: 'Count on our team for fast, helpful assistance with all your shipping needs.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/dedicated-customer-support.svg',
        alt: 'Dedicated Customer Support service'
      }
    }
  ]
}

export const forWhoSectionContent = {
  title: 'What Items Can You Ship with Us?',

  cards: [
    {
      id: 1,
      title: 'Suitcases & Duffel Bags',
      description: 'Ship all your travel luggage, regardless of size, weight, or volume.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/suitcases-duffel-bags.svg',
        alt: 'Suitcases & Duffel Bags service'
      }
    },
    {
      id: 2,
      title: 'Golf Clubs & Accessories',
      description: 'Send your golf equipment directly to the course for ultimate convenience.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/golf-clubs-accessories.svg',
        alt: 'Golf Clubs & Accessories service'
      }
    },
    {
      id: 3,
      title: 'Skis, Snowboards & Gear',
      description: 'Enjoy seamless delivery of winter sports gear to your preferred destination.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/skis-snowboards-gear.svg',
        alt: 'Skis, Snowboards & Gear service'
      }
    },
    {
      id: 4,
      title: 'Packed Boxes & Essentials',
      description: 'Perfect for books, clothing, seasonal gear, or other packed belongings.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/packed-boxes-essentials.svg',
        alt: 'Packed Boxes & Essentials service'
      }
    },
    {
      id: 5,
      title: 'Specialty Equipment',
      description: 'Transport musical instruments, electronics, or sports gear securely.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/specialty-equipment.svg',
        alt: 'Specialty Equipment service'
      }
    },
    {
      id: 6,
      title: 'Pickup and Delivery',
      description: 'Ship dorm essentials and school supplies directly to your campus address.',
      image: {
        src: '/images/v2/pages/main-pages/luggage-shipping/student-luggage.svg',
        alt: 'Pickup and Delivery service'
      }
    }
  ]
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'How far in advance should I schedule luggage shipping?',
      answer: 'For domestic trips, schedule 3–5 days prior; for international, 7–10 days ahead.'
    },
    {
      id: 2,
      question: 'Can I ship fragile or high-value items?',
      answer: 'Yes, but ensure items are packed securely, and consider additional coverage.'
    },
    {
      id: 3,
      question: 'Are there weight or size limits for luggage?',
      answer: 'Most luggage is accepted, but specific carrier limits may apply based on size.'
    },
    {
      id: 4,
      question: 'What happens if my luggage is delayed?',
      answer: 'Delays are rare, but if they occur, our team will assist in resolving them quickly.'
    },
    {
      id: 5,
      question: 'Can I ship luggage to multiple destinations?',
      answer: 'Yes, we support multi-destination shipments for added convenience.'
    },
    {
      id: 6,
      question: 'Do I need to be present for pickup?',
      answer: 'Not necessarily; leave your luggage in a secure location if pickup is scheduled.'
    }
  ]
}
